/**
 * format.js — Formatters frontend unifiés (P2-3).
 *
 * Point d'entrée unique pour :
 *   - shortAddress()   : troncature d'adresse wallet (remplace truncateWallet /
 *                        shortWallet / slices inline `0,6…-4` dispersés)
 *   - formatDate()     : date localisée sur la langue i18n courante
 *   - formatDateTime() : date + heure localisées sur la langue i18n courante
 *   - ré-export de l'API prix existante de ./formatPrice (formatMwei, formatUSDC,
 *     formatUSD, parseMwei, formatGwei3)
 *
 * NOTE (assumée, P2-3) : les dates autrefois figées en 'en-GB'/'en-US' suivent
 * désormais la langue i18n de l'utilisateur.
 */

import i18next from 'i18next';

/* ── Ré-export de l'API prix (source de vérité : formatPrice.js) ─────────── */
export { formatMwei, formatUSDC, formatUSD, parseMwei, formatGwei3 } from './formatPrice';

/**
 * shortAddress — Tronque une adresse wallet : "0x07Ac…b732".
 *
 * @param {string|null|undefined} addr - Adresse complète
 * @param {number} [lead=6]  - Nombre de caractères de tête conservés
 * @param {number} [tail=4]  - Nombre de caractères de queue conservés
 * @returns {string} Adresse tronquée avec ellipse "…", '' si addr est falsy,
 *                   l'adresse telle quelle si déjà assez courte.
 */
export function shortAddress(addr, lead = 6, tail = 4) {
  if (addr == null || typeof addr !== 'string') return '';
  if (addr.length <= lead + tail) return addr;
  return `${addr.slice(0, lead)}…${addr.slice(-tail)}`;
}

/** Langue courante de l'UI (i18next), repli sur la langue navigateur. */
function currentLocale() {
  return (
    (i18next && i18next.language) ||
    (typeof navigator !== 'undefined' && navigator.language) ||
    'en'
  );
}

/**
 * formatDate — Date localisée sur la langue i18n courante.
 *
 * @param {string|number|Date|null|undefined} date - Valeur acceptée par new Date()
 * @param {Intl.DateTimeFormatOptions} [opts] - Options Intl (défaut : date courte navigateur)
 * @returns {string} Date formatée, '' si date falsy, 'N/A' si invalide.
 */
export function formatDate(date, opts) {
  if (date == null || date === '') return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString(currentLocale(), opts);
}

/**
 * formatDateTime — Date + heure localisées sur la langue i18n courante.
 *
 * @param {string|number|Date|null|undefined} date - Valeur acceptée par new Date()
 * @param {Intl.DateTimeFormatOptions} [opts] - Options Intl (défaut : datetime navigateur)
 * @returns {string} Date/heure formatée, '' si date falsy, 'N/A' si invalide.
 */
export function formatDateTime(date, opts) {
  if (date == null || date === '') return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString(currentLocale(), opts);
}
